import { Alert, colors, CompanyLogoIcon, CompanyLogoStatus, ModalHeader, ProgressBar } from '@ui-components/neo.tax';

import { useAnalyticId } from '@neo.tax/shared/state';

import { ScrollableUnorderedList } from '../../../scrollable-unordered-list';
import { getProviderInfo } from '../utils';
import {
  StyledAutomationsIcon,
  StyledConnectProvider,
  StyledSyncingStack,
  StyledSyncingText,
} from './connect-provider.styled';
import { ConnectProviderActions } from './ConnectProviderActions';
import { ConnectProviderStatus } from './enums';
import { ConnectProviderSlideProps, ProviderAnalyticLiteralIds, UseProviderConnection } from './types';

const titleText = (status: ConnectProviderStatus): string => {
  switch (status) {
    case 'error': {
      return 'Connection Issue';
    }
    case 'syncing': {
      return 'Data Sync in Progress';
    }
    default: {
      return 'Connecting accounts...';
    }
  }
};

const subtitleText = ({
  numberOfFailedAccounts,
  numberOfInProgressAccounts,
  status,
}: {
  status: ConnectProviderStatus;
  numberOfFailedAccounts: number;
  numberOfInProgressAccounts: number;
}): string => {
  switch (status) {
    case 'success': {
      return 'Success';
    }
    case 'error': {
      return `${numberOfFailedAccounts} failed`; // When supporting multiple providers, change this to count the number of failures in providers
    }
    default: {
      return `${numberOfInProgressAccounts} in progress`; // When supporting multiple providers, change this to count of number in progress
    }
  }
};

const connectStatusToIconStatus = (connectStatus: ConnectProviderStatus): CompanyLogoStatus => {
  switch (connectStatus) {
    case ConnectProviderStatus.SUCCESS:
      return 'success';
    case ConnectProviderStatus.ERROR:
      return 'error';
    default:
      return 'default';
  }
};

type ConnectProviderProps = ConnectProviderSlideProps &
  UseProviderConnection & {
    close: () => void;
    restart: () => void;
    errorMessages?: string[];
  };

export const ConnectProvider = ({
  close,
  connect,
  connectStatus,
  errorMessages,
  provider,
  restart,
}: ConnectProviderProps): React.ReactElement => {
  const { analyticIds, fullName, logo, name } = getProviderInfo(provider);

  if (!analyticIds.tryAgainId) {
    throw new Error(`Please provide a 'try again' id for ${name}`);
  }
  const literalAnalyticIds: ProviderAnalyticLiteralIds = {
    connectId: useAnalyticId(analyticIds.connectId),
    cancelConnectionId: useAnalyticId(analyticIds.cancelConnectionId),
    tryAgainId: useAnalyticId(analyticIds.tryAgainId),
  };

  return (
    <StyledConnectProvider>
      <ModalHeader
        subtitle={subtitleText({
          status: connectStatus,
          numberOfFailedAccounts: connectStatus === ConnectProviderStatus.ERROR ? 1 : 0,
          numberOfInProgressAccounts: connectStatus === ConnectProviderStatus.ERROR ? 0 : 1,
        })}
        icon={<StyledAutomationsIcon />}
        iconBgColor={colors.blue[50]}
        title={titleText(connectStatus)}
      />
      {connectStatus === 'syncing' ? (
        <StyledSyncingStack direction="column">
          <StyledSyncingText as="p" fontSize="sm">
            {`Great news, we are importing your data from ${fullName} but have not finished yet. Please stay tuned.`}
          </StyledSyncingText>
          <ProgressBar />
        </StyledSyncingStack>
      ) : (
        <CompanyLogoIcon logo={logo} companyName={fullName} status={connectStatusToIconStatus(connectStatus)} />
      )}
      {connectStatus === ConnectProviderStatus.ERROR && (
        <Alert
          title="There were errors with your connection"
          subtitle={
            errorMessages ? (
              <ScrollableUnorderedList maxHeight={310}>
                {errorMessages.map((errorMessage, index) => (
                  <li key={index}>{errorMessage}</li>
                ))}
              </ScrollableUnorderedList>
            ) : (
              <p>
                Unfortunately, we ran into an unknown error. Please review the connection again or contact us for help.
              </p>
            )
          }
          severity="error"
        />
      )}

      <ConnectProviderActions
        connectProvider={connect}
        analyticIds={literalAnalyticIds}
        close={close}
        restart={restart}
        providerName={name}
        status={connectStatus}
      />
    </StyledConnectProvider>
  );
};
